// Which sync failures set a change aside instead of stopping sync: sending it
// again changes nothing, so it waits for the measurer's choice (refused.ts)
// rather than holding up every change behind it. Pure.
//  - CRM saying no to this user (403): the job doesn't open for them
//    (NOT_YOUR_JOB), or their role doesn't allow the change;
//  - the record is no longer in CRM (404 NOT_IN_CRM): someone removed it;
//  - its version was issued to the client or replaced (409 VERSION_FROZEN),
//    on another device or before this one had heard: it can never change now;
//  - its condition visit was validated (409 VISIT_VALIDATED): it waits until
//    the visit is reopened;
//  - a defect moved out of its order (409 DEFECT_STEP): Open, Rectified, Verified;
//  - the API refusing the change as sent (400): the same save is refused every
//    time, so retrying it only held up every change behind it (#710).

const HTTP_BAD_REQUEST = 400;
const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const HTTP_CONFLICT = 409;
const NOT_IN_CRM = 'NOT_IN_CRM';
const VERSION_FROZEN = 'VERSION_FROZEN';
const VISIT_VALIDATED = 'VISIT_VALIDATED';
const DEFECT_STEP = 'DEFECT_STEP';
const DEFAULT_REASON = 'CRM refused this change for your account.';
const GONE_REASON = 'This is no longer in CRM — someone removed it.';
const FROZEN_REASON = 'This version has been issued to the client or replaced, so it can’t change. Make a new version to change it.';
const DEFECT_STEP_REASON = 'A defect goes Open, then Rectified, then Verified: this change skipped a step. Discard it and try again.';
const BAD_REQUEST_REASON = 'CRM couldn’t take this change as it is. Try again after the app updates, or discard it.';
const VALIDATED_REASON ='This condition visit has been validated, so its ratings can’t change. Reopen the visit, then try again.';

/**
 * Whether a failed save leaves the record in conflict (kept, flagged, not sent again): changed elsewhere
 * (STALE_WRITE) or an accepted option that is locked. A last-save-wins row (a tick, an answer) never is —
 * a STALE_WRITE there only means it lost a race several times over, so it stays queued for the next sync.
 */
export function isConflict(err: unknown, lastSaveWins: boolean): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const { status, code } = err as { status?: unknown; code?: unknown };
  if (status !== HTTP_CONFLICT) return false;
  return code === 'OPTION_LOCKED' || (code === 'STALE_WRITE' && !lastSaveWins);
}

/** Changed elsewhere since this device's copy was loaded (409 STALE_WRITE). */
export function isStaleWrite(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const { status, code } = err as { status?: unknown; code?: unknown };
  return status === HTTP_CONFLICT && code === 'STALE_WRITE';
}

/** The reason to show for a change set aside; null for any other failure (offline, a conflict, a server fault). */
export function refusalReason(err: unknown): string | null {
  if (typeof err !== 'object' || err === null) return null;
  const { status, code, message } = err as { status?: unknown; code?: unknown; message?: unknown };
  const own = typeof message === 'string' && message.trim() ? message.trim() : null;
  if (status === HTTP_BAD_REQUEST) return own ?? BAD_REQUEST_REASON;
  if (status === HTTP_FORBIDDEN) return own ?? DEFAULT_REASON;
  if (status === HTTP_NOT_FOUND && code === NOT_IN_CRM) return own ?? GONE_REASON;
  if (status === HTTP_CONFLICT && code === VERSION_FROZEN) return own ?? FROZEN_REASON;
  if (status === HTTP_CONFLICT && code === VISIT_VALIDATED) return own ?? VALIDATED_REASON;
  if (status === HTTP_CONFLICT && code === DEFECT_STEP) return own ?? DEFECT_STEP_REASON;
  return null;
}
